import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Filter, Package, RefreshCw, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import AddProductModal from './AddProductModal';
import { apiService } from '../../api/api';

// Category reference data
const CATEGORY_MAP = {
  26: { name: "Bandage And Dressing", color: "#3B82F6" },
  27: { name: "Dba Products", color: "#BDAB32" },
  28: { name: "Infection Control", color: "#D3509C" },
  29: { name: "Laboratory Supplies", color: "#CC531E" },
  30: { name: "Orthopedic Supplies", color: "#28D23C" },
  31: { name: "Paper Products", color: "#15B6CB" },
  32: { name: "Pediatric products", color: "#406A72" },
  33: { name: "Personal Protection Products", color: "#3A2343" },
  34: { name: "Physical Therapy Equipment And Supplies", color: "#49C247" },
  35: { name: "Pulse oximete", color: "#68A68B" },
  36: { name: "Splints", color: "#C0BA07" },
  37: { name: "Underpads", color: "#213E6E" },
  38: { name: "Other", color: "#3B82F6" },
  139: { name: "other2", color: "#D8E2F3" },
  140: { name: "other2", color: "#D8E2F3" },
  141: { name: "other2", color: "#D8E2F3" },
  142: { name: "other2", color: "#D8E2F3" },
  143: { name: "other2", color: "#D8E2F3" },
  1: { name: "General", color: "#3B82F6" }
};

const PendingProductsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('edit');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [retryCount, setRetryCount] = useState(0);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2);
  };

  const getCategoryName = (categoryId, categoryName) => {
    if (categoryId && CATEGORY_MAP[categoryId]) {
      return CATEGORY_MAP[categoryId].name;
    }
    return categoryName || 'Uncategorized';
  };

  const getCategoryColor = (categoryId) => {
    if (categoryId && CATEGORY_MAP[categoryId]) {
      return CATEGORY_MAP[categoryId].color;
    }
    return '#718096';
  };

  const navigateTo = (path, state = null) => {
    if (state) {
      sessionStorage.setItem('navigationState', JSON.stringify(state));
    }
    window.location.href = path;
  };

  useEffect(() => {
    fetchPendingProducts();
  }, []);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategory]);

  const fetchPendingProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get('/Product/AllPending');
      let productsData = [];

      if (Array.isArray(response)) {
        productsData = response;
      } else if (response && Array.isArray(response.data)) {
        productsData = response.data;
      } else if (response && Array.isArray(response.products)) {
        productsData = response.products;
      }

      if (productsData.length === 0) {
        setProducts([]);
        setError(null);
        setLoading(false);
        return;
      }

      const transformedProducts = productsData.map(product => {
        const categoryId = product.medicalCategory || product.categoryId || product.category;
        const isOutOfStock = product.outOfStock === true;

        return {
          ...product,
          name: product.productName || product.name || 'Unnamed Product',
          category: getCategoryName(categoryId, product.categoryName),
          categoryId: categoryId,
          categoryColor: getCategoryColor(categoryId),
          price: parseFloat(product.salesPrice || product.price) || 0,
          hasFixedPrice: product.fixedPrice === true,
          fixedPrice: product.fixedPrice === true ? parseFloat(product.salePrice) || 0 : 0,
          stock: parseInt(product.openingStock || product.stock) || 0,
          outOfStock: isOutOfStock,
          displayStatus: isOutOfStock ? 'Out of Stock' : 'In Stock',
          sku: product.inventoryCode || product.sku || `PRD-${String(product.id).padStart(3, '0')}`,
          image: product.image || 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=100&h=100&fit=crop',
          hasEcommerce: !!product.eCommerceProductId && product.eCommerceProductId > 0,
          eCommerceProductId: product.eCommerceProductId || 0
        };
      });

      setProducts(transformedProducts);
      setError(null);
      setRetryCount(0);

    } catch (error) {
      let errorMessage = 'Failed to load pending products. ';
      if (error.response?.status === 401) {
        const token = localStorage.getItem('authToken');
        if (token) {
          errorMessage = 'Your session has expired. Attempting to reload without authentication...';
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('userId');
          setTimeout(() => fetchPendingProducts(), 1000);
          return;
        } else {
          errorMessage = 'Authentication error occurred. Retrying...';
          setTimeout(() => fetchPendingProducts(), 1000);
          return;
        }
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to view pending products.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Pending products API endpoint not found. Please contact support.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (!error.response) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else {
        errorMessage += error.response?.data?.message ||
          error.response?.data?.title ||
          error.message ||
          'Please try again.';
      }

      setError(errorMessage);
      setProducts([]);

    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchPendingProducts();
  };

  const handleEditProduct = async (product) => {
    try {
      let fullProduct = { ...product };

      if (product.eCommerceProductId && product.eCommerceProductId > 0) {
        try {
          const ecommerceProduct = await apiService.getEcommerceProductById(product.eCommerceProductId);
          fullProduct = {
            ...product,
            ...ecommerceProduct,
            inventoryData: product
          };
        } catch (ecomError) {
          console.error('Failed to fetch ecommerce product:', ecomError);
        }
      }

      const productIdForImages = fullProduct.id || product.id;

      try {
        const images = await apiService.getProductImages(productIdForImages);
        let imagesArray = [];
        if (Array.isArray(images)) {
          imagesArray = images;
        } else if (images && Array.isArray(images.data)) {
          imagesArray = images.data;
        }

        if (imagesArray.length > 0) {
          imagesArray.sort((a, b) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return (a.displayOrder || 0) - (b.displayOrder || 0);
          });
        }

        fullProduct.productImages = imagesArray;

      } catch (imgError) {
        console.error('Failed to fetch images:', imgError);
        fullProduct.productImages = [];
      }

      setSelectedProduct(fullProduct);
      setModalMode('edit');
      setIsModalOpen(true);
    } catch (error) {
      alert('Failed to load product details: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleViewProduct = async (product) => {
    try {
      let fullProduct = { ...product };

      if (product.eCommerceProductId && product.eCommerceProductId > 0) {
        try {
          const ecommerceProduct = await apiService.getEcommerceProductById(product.eCommerceProductId);
          fullProduct = {
            ...product,
            ...ecommerceProduct,
            inventoryData: product
          };
        } catch (ecomError) {
          console.error('Failed to fetch ecommerce product:', ecomError);
        }
      }

      const productIdForImages = fullProduct.id || product.id;

      try {
        const images = await apiService.getProductImages(productIdForImages);
        let imagesArray = [];
        if (Array.isArray(images)) {
          imagesArray = images;
        } else if (images && Array.isArray(images.data)) {
          imagesArray = images.data;
        }

        if (imagesArray.length > 0) {
          imagesArray.sort((a, b) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return (a.displayOrder || 0) - (b.displayOrder || 0);
          });
        }

        fullProduct.productImages = imagesArray;

      } catch (imgError) {
        console.error('Failed to fetch images:', imgError);
        fullProduct.productImages = [];
      }

      setSelectedProduct(fullProduct);
      setModalMode('view');
      setIsModalOpen(true);
    } catch (error) {
      alert('Failed to load product details: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteProduct = async (product) => {
    if (window.confirm(`Are you sure you want to delete "${product.name}"? This action cannot be undone.`)) {
      try {
        await apiService.deleteEcommerceProduct(product.id);
        alert('Product deleted successfully!');
        fetchPendingProducts();
      } catch (error) {
        alert('Failed to delete product: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleSaveProduct = async (productData, mode) => {
    try {
      if (mode === 'edit') {
        if (!productData.id || productData.id === 0) {
          alert('Invalid product ID. Cannot update product.');
          return; // Don't close modal, let user fix the issue
        }

        await apiService.updateEcommerceProduct(productData.id, productData);

        if (productData.newImages && productData.newImages.length > 0) {
          try {
            await apiService.bulkUploadProductImages(
              productData.id,
              productData.newImages,
              productData.primaryImageIndex || 0
            );
            alert('Product and images updated successfully!');
          } catch (imgError) {
            alert('Product updated but image upload failed: ' + (imgError.response?.data?.message || imgError.message));
          }
        } else {
          alert('Product updated successfully!');
        }

        // Only close modal and refresh on success
        await fetchPendingProducts();
        setIsModalOpen(false);
        setSelectedProduct(null);
      }
    } catch (error) {
      console.error('Save product error:', error);

      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
        navigateTo('/login');
        return;
      }

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.title ||
        error.message ||
        'Unknown error occurred';

      alert(`Failed to update product:\n\n${errorMessage}\n\nPlease check your input and try again.`);

      // Don't close modal or clear form on error - let user fix the issue
    }
  };

  const filteredProducts = products.filter((product) => {
    const rawSearch = searchTerm.toLowerCase();
    const search = rawSearch.replace(/[$₹,\s]/g, '');

    const isNumberSearch = !isNaN(search) && search !== '';

    const price = product.salePrice || product.price || 0;

    const priceMatch =
      price.toString().includes(search) ||
      price.toFixed(2).includes(search);

    const idMatch = String(product.id).includes(search);

    // ✅ FIX ADDED HERE
    const itemNoMatch = String(product.itemNo || '').includes(search);

    const matchesSearch = Object.entries(product).some(([key, value]) => {
      if (value === null || value === undefined) return false;

      if (typeof value === "number") {
        return value.toString().includes(search);
      }

      if (typeof value === "string") {
        return value.toLowerCase().includes(rawSearch);
      }

      return false;
    });

    const matchesCategory =
      filterCategory === 'all' || product.category === filterCategory;

    // 🔥 FIXED LOGIC
    if (isNumberSearch) {
      return (priceMatch || idMatch || itemNoMatch) && matchesCategory;
    }

    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(products.map(p => p.category))].sort();

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Loading State
  if (loading) {
    return (
      <div className="admin-content">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{
            display: 'inline-block',
            width: '50px',
            height: '50px',
            border: '4px solid #e2e8f0',
            borderTopColor: '#4361ee',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>
          <div style={{ fontSize: '18px', color: '#718096', marginBottom: '10px' }}>
            Loading pending products...
          </div>
          {retryCount > 0 && (
            <div style={{ fontSize: '14px', color: '#a0aec0' }}>
              Retry attempt {retryCount}
            </div>
          )}
        </div>
        <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
      </div>
    );
  }

  // Error State
  if (error) {
    const isAuthError = error.includes('Authentication') || error.includes('login') || error.includes('Session');

    return (
      <div className="admin-content">
        <div className="content-header">
          <div>
            <h1>Pending Products</h1>
            <p>Manage pending/inactive products</p>
          </div>
        </div>
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: isAuthError ? '#fee2e2' : '#fff3cd',
          borderRadius: '8px',
          border: `1px solid ${isAuthError ? '#ef4444' : '#ffc107'}`,
          margin: '20px 0'
        }}>
          <div style={{ marginBottom: '20px' }}>
            {isAuthError ? (
              <AlertCircle size={64} color="#dc2626" />
            ) : (
              <Package size={64} color="#856404" />
            )}
          </div>

          <h3 style={{
            color: isAuthError ? '#dc2626' : '#856404',
            marginBottom: '10px',
            fontSize: '24px',
            fontWeight: '600'
          }}>
            Unable to Load Pending Products
          </h3>

          <p style={{
            color: isAuthError ? '#dc2626' : '#856404',
            marginBottom: '20px',
            fontSize: '16px'
          }}>
            {error}
          </p>

          {isAuthError ? (
            <button
              className="primary-btn"
              onClick={() => navigateTo('/login')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Login to Continue
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="primary-btn"
                onClick={handleRetry}
                disabled={retryCount >= 3}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: retryCount >= 3 ? 0.5 : 1,
                  cursor: retryCount >= 3 ? 'not-allowed' : 'pointer'
                }}
              >
                <RefreshCw size={18} />
                {retryCount >= 3 ? 'Max Retries Reached' : 'Retry Loading Products'}
              </button>

              <button
                className="secondary-btn"
                onClick={() => navigateTo('/admin')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                Go to Dashboard
              </button>
            </div>
          )}

          {retryCount > 0 && retryCount < 3 && (
            <p style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>
              Retry attempt {retryCount} of 3
            </p>
          )}
        </div>
      </div>
    );
  }

  // Main Content
  return (
    <div className="admin-content">
      <div className="content-header">
        <div>
          <h1>Pending Products</h1>
          <p>Manage pending and inactive products</p>
        </div>
      </div>

      {/* Stats Cards */}
      {/* <div className="stats-grid" style={{ marginBottom: '30px' }}>
          <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
            <h3>Total Pending Products</h3>
            <div className="stat-value">{products.length}</div>
            <div className="stat-change" style={{ color: '#f59e0b' }}>Awaiting approval</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #4361ee' }}>
            <h3>In Stock</h3>
            <div className="stat-value">{products.filter(p => p.displayStatus === 'In Stock').length}</div>
            <div className="stat-change" style={{ color: '#4361ee' }}>Available inventory</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #ef4444' }}>
            <h3>Out of Stock</h3>
            <div className="stat-value">{products.filter(p => p.displayStatus === 'Out of Stock').length}</div>
            <div className="stat-change" style={{ color: '#ef4444' }}>Unavailable</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
            <h3>With E-commerce</h3>
            <div className="stat-value">{products.filter(p => p.hasEcommerce).length}</div>
            <div className="stat-change" style={{ color: '#10b981' }}>Linked products</div>
          </div>
        </div> */}

      {/* Filters and Search */}
      <div className="table-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search pending products by name or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <Filter size={18} />
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Table */}
      <div className="recent-section">
        {products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Package size={64} color="#cbd5e0" />
            <h3 style={{ color: '#4a5568', marginTop: '16px' }}>No Pending Products</h3>
            <p style={{ color: '#718096' }}>There are no pending products in the system</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Search size={64} color="#cbd5e0" />
            <h3 style={{ color: '#4a5568', marginTop: '16px' }}>No Products Found</h3>
            <p style={{ color: '#718096' }}>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <>
            {/* Pagination Info and Items Per Page */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              padding: '0 4px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ fontSize: '14px', color: '#718096' }}>
                Showing {startIndex + 1} to {Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} pending products
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '14px', color: '#718096' }}>Items per page:</label>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    backgroundColor: 'white'
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Product Name</th>
                    <th>Item Number</th>
                    <th>Category</th>
                    <th>Fixed Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentProducts.map((product) => (
                    <tr key={product.id}>
                      <td>{product.id}</td>
                      <td>{product.name}</td>
                      <td>{product.itemNo || 'N/A'}</td>
                      <td>{product.category}</td>
                      <td>
                        {product.hasFixedPrice && product.fixedPrice > 0
                          ? `$${formatPrice(product.fixedPrice)}`
                          : 'N/A'
                        }
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: product.displayStatus === 'In Stock' ? '#d1fae5' : '#fee2e2',
                          color: product.displayStatus === 'In Stock' ? '#065f46' : '#991b1b'
                        }}>
                          {product.displayStatus}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn-icon edit"
                            title="Edit Product"
                            onClick={() => handleEditProduct(product)}
                          >
                            <Edit2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '24px',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    backgroundColor: currentPage === 1 ? '#f7fafc' : 'white',
                    color: currentPage === 1 ? '#cbd5e0' : '#4a5568',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                <div style={{ display: 'flex', gap: '4px' }}>
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span
                        key={`ellipsis-${index}`}
                        style={{
                          padding: '8px 12px',
                          color: '#718096',
                          fontSize: '14px'
                        }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          backgroundColor: currentPage === page ? '#4361ee' : 'white',
                          color: currentPage === page ? 'white' : '#4a5568',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: currentPage === page ? '600' : '500',
                          minWidth: '40px'
                        }}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    backgroundColor: currentPage === totalPages ? '#f7fafc' : 'white',
                    color: currentPage === totalPages ? '#cbd5e0' : '#4a5568',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Product Modal */}
      <AddProductModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProduct(null);
        }}
        onSave={handleSaveProduct}
        mode={modalMode}
        productData={selectedProduct}
      />
    </div>
  );
};

export default PendingProductsPage;